'use client';

import { useState, useEffect } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import type { Language } from '@/lib/translations';

interface OfflineIndicatorProps {
  lang?: Language;
}

export function OfflineIndicator({ lang = 'en' }: OfflineIndicatorProps) {
  const [isOnline, setIsOnline] = useState(true);
  const isFetching = useIsFetching();

  useEffect(() => {
    // 检测在线/离线状态
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    // 初始状态
    setIsOnline(navigator.onLine);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 只在离线时显示提示
  if (isOnline) {
    return null;
  }

  const messages = {
    en: {
      offline: 'Offline Mode',
      message: 'Showing cached data. Data will refresh automatically when connection is restored.',
    },
    cn: {
      offline: '离线模式',
      message: '正在显示缓存数据。网络恢复后将自动刷新。',
    },
  };

  const t = messages[lang];

  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 rounded-r">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-yellow-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <p className="text-sm font-medium text-yellow-800">{t.offline}</p>
          <p className="mt-1 text-sm text-yellow-700">{t.message}</p>
        </div>
      </div>
    </div>
  );
}
